import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  SvgIcon,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';
import { Autocomplete, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { isEmail } from 'class-validator';
import React, { useEffect, useState } from 'react';

import { ReactComponent as SlackLogo } from '../../../assets/slack.svg';
import {
  ChannelType,
  ItemType,
  SlackChannel,
  useDomainSlackChannelsLazyQuery,
  useShareMutation,
} from '../../../graphql/gen/types';
import { logAnalytics } from '../shared/logAnalytics';

type State =
  | { channelType: 'slack'; slackQuery: string; selectedChannel: SlackChannel | null }
  | {
      channelType: 'email';
      email: string;
    };

type Props = {
  itemId: string;
  itemType: 'post' | 'question';
  anchorEl: HTMLElement | null;
  handleClose: () => void;
};

export function SharePopover({ itemId, itemType, anchorEl, handleClose }: Props): React.ReactElement {
  const classes = useStyles();
  const [state, setState] = useState<State>({ channelType: 'slack', slackQuery: '', selectedChannel: null });
  const [
    getSlackChannels,
    { data: slackChannelData, loading: slackChannelsLoading },
  ] = useDomainSlackChannelsLazyQuery();
  useEffect(() => {
    if (anchorEl != null) getSlackChannels();
  }, [anchorEl, getSlackChannels]);
  const [shareMutation, { loading }] = useShareMutation();
  const open = anchorEl != null;
  const dialogTitle = `Share ${itemType}`;
  const domainChannels = slackChannelData?.currentUser?.domain?.slackChannels?.results;
  const emailError = state.channelType === 'email' && state.email !== '' && !isEmail(state.email);

  const isFormComplete = ((): boolean => {
    switch (state.channelType) {
      case 'email':
        return state.email !== '' && !emailError;
      case 'slack':
        return state.selectedChannel != null;
      default:
        return false;
    }
  })();

  const handleShare = (): void => {
    const shareInput = {
      itemId,
      itemType: itemType === 'post' ? ItemType.Post : ItemType.Question,
      channelId: state.channelType === 'slack' ? state.selectedChannel?.id || '' : state.email,
      channelType: state.channelType === 'slack' ? ChannelType.Slack : ChannelType.Gmail,
    };
    shareMutation({ variables: { shareInput } })
      .then(() => {
        setState({ channelType: 'slack', slackQuery: '', selectedChannel: null });
        handleClose();
      })
      .then(() => logAnalytics({ name: itemType === 'post' ? 'post shared' : 'faq shared', details: { itemId } }));
  };

  const handleSlackQueryChange = (_: React.ChangeEvent<unknown>, value: string): void => {
    if (state.channelType === 'slack') setState({ ...state, slackQuery: value });
  };

  const handleSelectSlackChannel = (_: React.ChangeEvent<unknown>, value: SlackChannel | null): void => {
    if (value) setState({ channelType: 'slack', slackQuery: `#${value.name}`, selectedChannel: value });
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setState({ channelType: 'email', email: event.currentTarget.value });
  };

  const handleSelectChannelType = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: string | null): void => {
    if (value != null) {
      switch (value) {
        case 'slack': {
          setState({ channelType: 'slack', slackQuery: '', selectedChannel: null });
          break;
        }
        case 'email': {
          setState({ channelType: 'email', email: '' });
          break;
        }
        default: {
          throw new Error('unknown channel type');
        }
      }
    }
  };

  return (
    <Popover
      id={open ? 'share-popover' : undefined}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers className={classes.content}>
        <ToggleButtonGroup
          value={state.channelType}
          exclusive
          className={classes.buttonGroup}
          onChange={handleSelectChannelType}
          aria-label="share channel"
        >
          <ToggleButton value="slack" aria-label="slack">
            <SvgIcon viewBox="0 0 270 270" fontSize="large">
              <SlackLogo />
            </SvgIcon>
            Slack
          </ToggleButton>
          <ToggleButton value="email" aria-label="email">
            <EmailIcon /> Email
          </ToggleButton>
        </ToggleButtonGroup>
        {state.channelType === 'email' && (
          <div>
            <TextField
              required
              error={emailError}
              label="Email"
              variant="outlined"
              className={classes.typeTextField}
              value={state.email}
              onChange={handleEmailChange}
            />
          </div>
        )}
        {state.channelType === 'slack' && slackChannelsLoading && (
          <CircularProgress className={classes.progress} color="inherit" size={20} />
        )}
        {state.channelType === 'slack' && !slackChannelsLoading && (
          <Autocomplete
            clearOnEscape
            noOptionsText="No such channel"
            onChange={handleSelectSlackChannel}
            value={state.selectedChannel}
            options={domainChannels || []}
            onInputChange={handleSlackQueryChange}
            inputValue={state.slackQuery}
            getOptionLabel={(option): string => `#${option.name}`}
            renderInput={(params: TextFieldProps): React.ReactElement => (
              <TextField
                {...params}
                required
                label="Slack channel"
                variant="outlined"
                className={classes.typeTextField}
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleShare} disabled={!isFormComplete || loading}>
          Share
        </Button>
      </DialogActions>
    </Popover>
  );
}

const useStyles = makeStyles((theme) => ({
  content: {
    width: 600,
  },
  buttonGroup: {
    marginBottom: theme.spacing(2),
  },
  typography: {
    padding: theme.spacing(2),
  },
  typeTextField: { width: 300, marginRight: theme.spacing(1) },
  progress: { margin: theme.spacing(2) },
}));
